import type { ConfigProviderProps } from 'antd';
import { theme } from 'antd';
import { createStyles } from 'antd-style';
import { useMemo } from 'react';
import { palette } from './colors';

const useStyles = createStyles(() => {
  return {};
});

const useIssueTheme = () => {
  const { styles } = useStyles();

  return useMemo<ConfigProviderProps>(
    () => ({
      theme: {
        algorithm: theme.defaultAlgorithm,
        token: {
          colorPrimary: '#09090b',
          colorSuccess: '#00bc7d',
          colorError: '#fb2c36',
          colorInfo: '#09090b',
          colorLink: '#2b7fff',
          colorTextBase: '#09090b',
          colorBgBase: '#ffffff',
          fontFamily:
            "'Pretendard Variable', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif",
          fontSize: 14,
          fontSizeSM: 13,
          fontSizeLG: 16,
          fontSizeXL: 18,
          fontSizeHeading1: 36,
          fontSizeHeading2: 24,
          fontSizeHeading3: 18,
          fontSizeHeading4: 16,
          fontSizeHeading5: 15,
          fontWeightStrong: 600,
          lineHeight: 1.5714,
          lineHeightSM: 1.5385,
          lineHeightLG: 1.5,
          lineHeightHeading1: 1.2222,
          lineHeightHeading2: 1.3333,
          lineHeightHeading3: 1.5556,
          lineHeightHeading4: 1.5,
          lineHeightHeading5: 1.4667,
          borderRadius: 4,
          borderRadiusSM: 4,
          borderRadiusLG: 8,
          colorPrimaryHover: '#3f3f47',
          colorText: '#09090b',
          colorTextSecondary: '#71717b',
          colorTextTertiary: '#9f9fa9',
          colorTextQuaternary: '#d4d4d8',
          colorTextDisabled: '#d4d4d8',
          colorTextPlaceholder: '#9f9fa9',
          colorTextDescription: '#71717b',
          colorTextLightSolid: '#ffffff',
          colorIcon: '#09090b',
          colorBgContainer: '#ffffff',
          colorBgLayout: '#ffffff',
          colorBgElevated: '#ffffff',
          colorBgSpotlight: '#09090b',
          colorBgMask: 'rgba(0, 0, 0, 0.6)',
          colorBgContainerDisabled: '#f4f4f5',
          colorBorder: '#e4e4e7',
          colorBorderSecondary: '#ececee',
          colorFill: '#d4d4d8',
          colorFillSecondary: '#ececee',
          colorFillTertiary: '#f4f4f5',
          colorFillQuaternary: '#fafafa',
          colorSuccessBg: '#ecfdf5',
          colorSuccessText: '#009966',
          colorSuccessBorder: '#00bc7d',
          colorWarningBg: '#fefce8',
          colorWarningText: '#894b00',
          colorWarningBorder: '#d08700',
          colorErrorBg: '#fef2f2',
          colorErrorText: '#fb2c36',
          colorErrorBorder: '#ffa2a2',
          colorInfoBg: '#eff6ff',
          colorInfoText: '#2b7fff',
          colorInfoBorder: '#51a2ff',
          colorWarning: '#f0b100',
        },
        components: {
          // Base Components
          // https://ant.design/components/layout#design-token
          Layout: {
            headerBg: '#4d0218',
            headerColor: '#ffffff',
            siderBg: '#f4f4f5',
            bodyBg: '#ffffff',
            headerHeight: 54,
            headerPadding: '0 16px',
            lightSiderBg: '#f4f4f5',
            colorText: '#18181b',
            // colorSiderBarBorderRight: '#e4e4e7',
            // colorMainHeaderBorderBottom: '#ececec',
            // colorSiderSelectedBg: '#d4d4d8',
          },
          // https://ant.design/components/menu#design-token
          Menu: {
            itemHoverBg: 'rgba(9,9,11,0.05)',
            itemBorderRadius: 6,
            itemSelectedBg: 'rgba(9, 9, 11, 0.05)',
            itemSelectedColor: '#09090b',
          },
          // https://ant.design/components/select#design-token
          Select: {
            optionSelectedBg: 'rgba(9,9,11,0.05)',
            optionSelectedColor: 'rgb(9,9,11)',
            optionActiveBg: 'rgba(9,9,11,0.05)',
          },
          // https://ant.design/components/transfer#design-token
          Transfer: {
            controlItemBgActive: 'rgba(9,9,11,0.05)', // global
            controlItemBgActiveHover: 'rgba(9,9,11,0.05)', // global
            controlItemBgHover: 'rgba(9,9,11,0.05)', // global
          },
          // https://ant.design/components/calendar#design-token
          Calendar: {
            controlItemBgActive: 'rgba(9,9,11,0.05)', // global
          },
          // https://ant.design/components/cascader#design-token
          Cascader: {
            optionSelectedBg: 'rgba(9,9,11,0.05)',
          },
          // https://ant.design/components/alert#design-token
          Alert: {
            colorInfo: 'rgb(43,127,255)', // lobal
            colorIcon: 'rgb(113,113,123)', // global
          },
          // https://ant.design/components/pagination#design-token
          Pagination: {
            colorBgTextHover: 'rgba(9,9,11,0.05)', // global
            colorTextDisabled: 'rgb(159,159,169)', // global
            itemActiveBgDisabled: 'rgba(255,255,255,0.15)', // global
          },
          // https://ant.design/components/rate#design-token
          Rate: {
            starColor: 'rgb(253,199,0)',
          },
          // https://ant.design/components/form#design-token
          Form: {
            itemMarginBottom: 16,
          },
          // https://ant.design/components/modal#design-token
          Modal: {
            titleLineHeight: 1.8,
            headerMarginBottom: 20, // potential
            footerPadding: '0', // potential
            footerMarginTop: '24px', // potential
            contentPadding: '32px 32px 24px 32px', // potential
          },
          // https://ant.design/components/avatar#design-token
          Avatar: {
            groupBorderColor: '#D4D4D8',
            containerSize: 32,
            colorTextPlaceholder: '#71717b', // global
          },
          // custom
          custom: {
            // ── Background ──
            colorBgContainerSecondary: '#fafafa',
            colorBgContainerTertiary: '#f4f4f5',
            colorBgContainerQuaternary: '#e4e4e7',
            colorBgContainerSelected: '#eff6ff',
            colorBgTextHover: 'rgba(9, 9, 11, 0.05)',
            colorBgTextSelected: 'rgba(9, 9, 11, 0.05)',
            colorBgTextSelectedStrong: '#d4d4d8',
            // ── Text ──
            colorTextAccent: '#2b7fff',
            // ── Icon ──
            colorIconSecondary: '#71717b',
            colorIconTertiary: '#9f9fa9',
            colorIconLight: '#ffffff',
            colorIconAccent: '#2b7fff',
            colorIconDisabled: '#d4d4d8',

            // ── Border ──
            colorBorderStrong: '#d4d4d8',
            colorBorderDisabled: '#ececee',

            // ── Info ──
            colorInfoBorderSecondary: '#dbeafe',
            // ── Error ──
            colorErrorBorderSecondary: '#ffe2e2',
            colorErrorTextDisabled: palette.red[200],
            // ── GNB ──
            gnb: {
              colorGnbAdminBg: '#09090b',
              colorGnbCounselBg: '#162456',
              colorGnbCounselIcon: '#2b7fff',
              colorGnbVocBg: '#002c22',
              colorGnbVocIcon: '#00bc7d',
              colorGnbIssueBg: '#4d0218',
              colorGnbIssueIcon: '#ff6467',
              colorGnbSelectBg: 'rgba(255, 255, 255, 0.2)',
              colorGnbSelectHoverBg: 'rgba(255, 255, 255, 0.3)',
            },
            // ── Alert ──
            alert: {
              alertInfoText: palette.blue[500],
              alertSuccessText: palette.emerald[600],
              alertWarningText: palette.yellow[800],
              alertCloseIcon: palette.neutral[500],
            },
            // ── Avatar ──
            avatar: {
              colorAvatarDefaultBg: '#71717b',
              colorAvatarOrangeBg: '#f54900',
              colorAvatarPinkBg: '#fb64b6',
              colorAvatarTealBg: '#00bba7',
              colorAvatarDarkBg: '#09090b',
              colorAvatarBorder: '#d4d4d8',
            },
            // ── Badge ──
            badge: {
              colorBadgeOrangeBg: '#ffedd4',
              colorBadgeOrangeText: '#f54900',
              colorBadgeGreyBg: '#e4e4e7',
              colorBadgeGreyText: '#71717b',
              colorBadgePurpleBg: '#f3e8ff',
              colorBadgePurpleText: '#9810fa',
              colorBadgeSkyBg: '#dff2fe',
              colorBadgeSkyText: '#0084d1',
              colorBadgeGreenBg: '#f0fdf4',
              colorBadgeGreenText: '#008236',
              colorBadgeIndigoBg: '#eef2ff',
              colorBadgeIndigoText: '#4f39f6',
              colorBadgeAmberBg: '#fffbeb',
              colorBadgeAmberText: '#e17100',
              colorBadgeRedBg: '#fef2f2',
              colorBadgeRedText: '#fb2c36',
              colorBadgeNeutralBg: '#ffffff',
              colorBadgeNeutralText: '#71717b',
              colorBadgeNeutralBorder: '#e4e4e7',
              colorBadgeRoleBg: palette.white,
              colorBadgeRoleIcon: palette.blue[500],
            },
            // ── Button ──
            button: {
              hoverBg: 'rgba(9, 9, 11, 0.05)',
              rowActionBtnBg: palette.neutral[200],
              circleBtnBg: palette.white,
              circleBtnBorder: palette.neutral[200],
              textBtnSecondaryColor: palette.neutral[500],
              colorErrorTextDisabled: palette.red[200],
              colorErrorBorderHover: palette.red[300],
              colorErrorBorderDisabled: palette.red[100],
            },
            // ── Checkbox ──
            checkbox: {
              colorTextDisabled: palette.neutral[950],
            },
            // ── DatePicker ──
            datePicker: {
              disabledBg: palette.neutral[100],
              itemHoverBg: 'rgba(9, 9, 11, 0.05)',
              itemSelectedBg: palette.neutral[950],
              cellTodayColor: palette.blue[500],
              cellTextDisabled: 'rgba(43, 127, 255, 0.5)',
              cellHolidayColor: palette.red[500],
              headerHolidayColor: palette.red[500],
            },
            // ── Filetype ──
            filetype: {
              colorFileImageBg: '#1447e6',
              colorFileVideoBg: '#ec003f',
              colorFileExcelBg: '#005f5a',
              colorFilePdfBg: '#e7000b',
              colorFileGifBg: '#ff6900',
              colorFilePptBg: '#ca3500',
              colorFileMp4Bg: '#f6339a',
              colorFilePsBg: '#0f172b',
              colorFilePsIcon: '#51a2ff',
              colorFileHwpBg: '#0084d1',
              colorFileTxtBg: '#71717b',
              colorFileZipBg: '#71717b',
              colorFileAiBg: '#461901',
              colorFileAiIcon: '#ffb900',
              colorFileIconInvert: '#ffffff',
            },
            // ── FilterSelect ──
            filterSelect: {
              selectorBg: palette.white,
              selectorHoverBg: palette.neutral[100],
              activeBorderColor: palette.neutral[950],
              selectorLabelColor: palette.neutral[500],
              selectorValueColor: palette.neutral[950],
            },
            // ── Input ──
            input: {
              readOnlyBg: palette.neutral[100],
              disabledBg: palette.neutral[100],
            },
            // ── Menu ──
            menu: {
              itemSelectedBgStrong: palette.neutral[300],
              itemCountColor: palette.blue[500],
              colorSplit: palette.neutral[200],
            },
            // ── Modal ──
            modal: {
              subTitleColor: palette.neutral[500],
            },
            // ── Pagination ──
            pagination: {
              itemActiveBorderColor: palette.neutral[200],
            },
            // ── Select ──
            select: {
              hoverBg: palette.neutral[100],
              readOnlyBg: palette.neutral[100],
              textHoverBg: 'rgba(9, 9, 11, 0.05)',
            },
            // ── Spin ──
            spin: {
              activeColor: palette.neutral[950],
              trackColor: palette.neutral[150],
            },
            // ── Steps ──
            steps: {
              currentBg: palette.neutral[950],
              waitBg: palette.neutral[300],
              finishBg: palette.neutral[300],
            },
            // ── Switch ──
            switch: {
              switchBg: palette.neutral[300],
              switchActiveBg: palette.neutral[950],
            },
            // ── Tabs ──
            tabs: {
              itemDisabledColor: palette.neutral[300],
            },
            // ── Tag ──
            tag: {
              hoverBg: 'rgba(9, 9, 11, 0.05)',
            },
            // ── Chart ──
            chart: {
              colorChartBlue: '#2b7fff',
              colorChartPurple: '#9810fa',
              colorChartYellow: '#f0b100',
              colorChartGrid: '#e4e4e7',
            },
          },
        },
      },
      button: {},
      input: {},
      select: {},
    }),
    [styles]
  );
};

export default useIssueTheme;
